import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Apartment,
  ApartmentDetails,
  ApartmentRentalState,
  MarkerData,
  ApartmentSortMode,
} from "../../../types/apartments";
import { CategoryNotice } from "../../../api/apartment/categoryOutcome";

const initialState: ApartmentRentalState = {
  locationApartmentList: [],
  selectedApartment: null,
  step: 0,
  nextEnabled: false,
  isMapOpen: false,
  apartmentDetails: null,
  numberOfGuest: 1,
  selectedMarker: null,
  sortMode: "RECOMMENDED",
  apartmentsLoadFailed: false,
  // `false` at rest: on a cold mount nobody has searched yet, which is NOT the
  // same as "no venues match" even though the list is `[]` in both cases.
  apartmentsSearchAnswered: false,
  categoryNotice: null,
  categoryRejectedOnEntry: false,
  enteredViaLink: false,
};

const priceOf = (apartment: Apartment): number =>
  typeof apartment.price === "number" ? apartment.price : Number(apartment.price) || 0;

/**
 * #3016: the tenant's manual order — placed rooms first, by position; unplaced
 * ones after them, left to the next key. MIRROR of `compareSortOrder` in
 * `new-backend/app/src/lib/manualSortOrder.ts` (same rule as the backends'
 * ORDER BY sort_order NULLS LAST); changed together.
 */
const compareSortOrder = (a?: number | null, b?: number | null): number => {
  const aPlaced = typeof a === "number";
  const bPlaced = typeof b === "number";
  if (aPlaced && bPlaced) return (a as number) - (b as number);
  if (aPlaced) return -1;
  if (bPlaced) return 1;
  return 0;
};

/**
 * The list's order for a sort mode. RECOMMENDED (the default) is the tenant's
 * manual order, then the price ascending — so a tenant that never reordered
 * sees exactly the price-ascending default it had before.
 */
export const compareApartments = (mode: ApartmentSortMode | undefined) => {
  const collator = new Intl.Collator(undefined, { sensitivity: "base" });
  const byName = (a: Apartment, b: Apartment) => collator.compare(a.name, b.name);
  const byPriceAsc = (a: Apartment, b: Apartment) => priceOf(a) - priceOf(b) || byName(a, b);
  return (a: Apartment, b: Apartment): number => {
    switch (mode) {
      case "RECOMMENDED":
        return compareSortOrder(a.sortOrder, b.sortOrder) || byPriceAsc(a, b);
      case "PRICE_ASC":
        return byPriceAsc(a, b);
      case "PRICE_DESC":
        return priceOf(b) - priceOf(a) || byName(a, b);
      case "NAME_DESC":
        return collator.compare(b.name, a.name);
      case "NAME_ASC":
      default:
        return byName(a, b);
    }
  };
};

const apartmentRentalSlice = createSlice({
  name: "apartmentRental",
  initialState,
  reducers: {
    setAllApartment: (state, action: PayloadAction<Apartment[]>) => {
      state.apartmentsLoadFailed = false;
      // Set before the defensive return below, not after: a payload we could
      // not use is still an answer that arrived, and leaving this `false` would
      // hang the step on a loader with nothing left to end it.
      state.apartmentsSearchAnswered = true;
      // Defensive: Guard in case the action payload is unexpectedly undefined / null
      if (!action.payload || !Array.isArray(action.payload)) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("setAllApartment: Payload is empty or not an array – setting empty list.", action.payload);
        }
        state.locationApartmentList = [];
        return; // Nothing to sort
      }

      state.locationApartmentList = action.payload.slice();
      // apply current sorting mode immediately for consistent initial display
      if (state.sortMode && state.locationApartmentList.length > 0) {
        state.locationApartmentList.sort(compareApartments(state.sortMode));
      }
    },
    // #1970: an empty result list and a search that never came back render
    // identically without this, so a backend failure reads as "nothing here".
    apartmentsLoadStarted: (state) => {
      state.apartmentsLoadFailed = false;
      // A re-search re-opens the question, so the previous list stops counting
      // as the answer to it.
      state.apartmentsSearchAnswered = false;
      // The notice explains ONE search's answer; it must not outlive it onto
      // the next one's (#2764 F9/F10).
      state.categoryNotice = null;
    },
    apartmentsLoadFailed: (state) => {
      state.apartmentsLoadFailed = true;
      state.categoryNotice = null;
      // A failure IS an answer. Without this the step would prefer the failure
      // branch anyway, but the flag would stay `false` forever and any later
      // reader would see a search that never came back.
      state.apartmentsSearchAnswered = true;
    },
    categoryNoticeSet: (state, action: PayloadAction<CategoryNotice | null>) => {
      state.categoryNotice = action.payload;
    },
    categoryRejectedOnEntrySet: (state, action: PayloadAction<boolean>) => {
      state.categoryRejectedOnEntry = action.payload;
    },
    enteredViaLinkSet: (state, action: PayloadAction<boolean>) => {
      state.enteredViaLink = action.payload;
    },
    setIsMapOpen: (state, action: PayloadAction<boolean>) => {
      state.isMapOpen = action.payload;
    },
    setSelectedApartment: (state, action: PayloadAction<Apartment>) => {
      state.selectedApartment = action.payload;
    },
    clearSelectedApartment: (state) => {
      state.selectedApartment = null;
      state.selectedMarker = null;
    },
    navigatedToPreviousStep: (state) => {
      if (state.isMapOpen) {
        state.isMapOpen = false;
      }
      state.step -= 1;
    },
    setNextButtonClick: (state) => {
      state.step += 1;
    },
    setApartmentDetails: (state, action: PayloadAction<ApartmentDetails>) => {
      state.apartmentDetails = action.payload;
    },
    setNumberOfGuest: (state, action: PayloadAction<number>) => {
      state.numberOfGuest = action.payload;
    },
    setSelectedMarker: (state, action: PayloadAction<MarkerData>) => {
      state.selectedMarker = action.payload;
    },
    sortApartment: (state, action: PayloadAction<ApartmentSortMode | "ASC" | "DESC">) => {
      // Backwards compatibility: map legacy values
      const legacy = action.payload === "ASC" || action.payload === "DESC";
      const mode: ApartmentSortMode = legacy
        ? action.payload === "ASC"
          ? "PRICE_ASC"
          : "PRICE_DESC"
        : (action.payload as ApartmentSortMode);
      state.sortMode = mode;
      state.locationApartmentList.sort(compareApartments(mode));
    },
    bookingCreationCompleted: () => initialState,
    setVenueReservationSuccess: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
    },
    apartmentStepSet: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
    },
  },
 
});

export const {
  setAllApartment,
  apartmentsLoadStarted,
  apartmentsLoadFailed,
  categoryNoticeSet,
  categoryRejectedOnEntrySet,
  enteredViaLinkSet,
  navigatedToPreviousStep,
  setSelectedMarker,
  clearSelectedApartment,
  setSelectedApartment,
  setNextButtonClick,
  setIsMapOpen,
  setApartmentDetails,
  setNumberOfGuest,
  bookingCreationCompleted,
  sortApartment,
  setVenueReservationSuccess,
  apartmentStepSet,
} = apartmentRentalSlice.actions;
export default apartmentRentalSlice.reducer;
